import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import { BottomOrRegularModal } from '@popsure/dirty-swan';
import { CashbackData } from 'components/PrivateQuoteWidget/FamilyCalculator/model';

import styles from './FamilyCashbackModal.module.scss';

export const FamilyCashbackModal = ({
  mainCashbackData,
  spouseCashbackData,
  childCashbackDataArray,
  isOpen,
  onClose,
  title,
}: {
  mainCashbackData?: CashbackData;
  spouseCashbackData?: CashbackData;
  childCashbackDataArray?: CashbackData[];
} & Omit<React.ComponentProps<typeof BottomOrRegularModal>, 'children'>) => {
  const mainMonthlyCashback = mainCashbackData?.guaranteedMonthlyCashback ?? 0;
  const spouseMonthlyCashback =
    spouseCashbackData?.guaranteedMonthlyCashback ?? 0;

  const childrenMonthlyCashbackValues = childCashbackDataArray?.map(
    ({ guaranteedMonthlyCashback }) => guaranteedMonthlyCashback
  );
  const childrenTotalMonthlyCashback =
    childrenMonthlyCashbackValues?.reduce(
      (sum, guaranteedMonthlyCashback) =>
        sum + (guaranteedMonthlyCashback ?? 0),
      0
    ) ?? 0;

  const totalMonthlyCashback =
    mainMonthlyCashback + spouseMonthlyCashback + childrenTotalMonthlyCashback;

  const mainFirstYearNoClaimsBonus =
    mainCashbackData?.firstYearNoClaimsBonus ?? 0;
  const spouseFirstYearNoClaimsBonus =
    spouseCashbackData?.firstYearNoClaimsBonus ?? 0;
  const childrenFirstYearNoClaimsBonus =
    childCashbackDataArray?.reduce(
      (sum, { firstYearNoClaimsBonus }) => sum + (firstYearNoClaimsBonus ?? 0),
      0
    ) ?? 0;

  const totalFirstYearNoClaimsBonus =
    mainFirstYearNoClaimsBonus +
    spouseFirstYearNoClaimsBonus +
    childrenFirstYearNoClaimsBonus;

  const mainTotalPotentialCashback =
    mainCashbackData?.totalPotentialCashback ?? 0;
  const spouseTotalPotentialCashback =
    spouseCashbackData?.totalPotentialCashback ?? 0;
  const childrenTotalPotentialCashback =
    childCashbackDataArray?.reduce(
      (sum, { totalPotentialCashback }) => sum + (totalPotentialCashback ?? 0),
      0
    ) ?? 0;

  const totalPotentialCashback =
    mainTotalPotentialCashback +
    spouseTotalPotentialCashback +
    childrenTotalPotentialCashback;

  const mainYearlyNoClaimsBonuses = mainCashbackData?.yearlyNoClaimsBonuses;
  const spouseYearlyNoClaimsBonuses = spouseCashbackData?.yearlyNoClaimsBonuses;
  const childrenYearlyNoClaimsBonuses = childCashbackDataArray?.reduce(
    (acc: number[][], { yearlyNoClaimsBonuses }) => [
      ...acc,
      yearlyNoClaimsBonuses,
    ],
    []
  );

  const years = [1, 2, 3, 4, 5];

  const yearlyNoClaimsBonuses = years.map((year) => {
    const index = year - 1;

    let sum = 0;

    if (mainYearlyNoClaimsBonuses) {
      sum += mainYearlyNoClaimsBonuses[index];
    }

    if (spouseYearlyNoClaimsBonuses) {
      sum += spouseYearlyNoClaimsBonuses[index];
    }

    if (childrenYearlyNoClaimsBonuses) {
      childrenYearlyNoClaimsBonuses.forEach((childXYearlyNoClaimsBonuses) => {
        sum += childXYearlyNoClaimsBonuses[index];
      });
    }

    return sum;
  });

  return (
    <BottomOrRegularModal title={title} isOpen={isOpen} onClose={onClose}>
      <div className="p24">
        <p className="p-p">
          Depending on how many claims you make, you may get some money back
          from your insurance.
        </p>
        <div className={`mt32 mb16 d-flex jc-between ${styles.row}`}>
          <p className="p-h3">Monthly cashback</p>
          <p className="p-h3">
            {englishFormattedEuroCurrency(totalMonthlyCashback, true)}
          </p>
        </div>
        <p className="p-p">
          The cashback is transferred to your bank account each month. When you
          submit medical bills, you must use that cashback to pay for them —
          except for preventative check-ups at the dentist or general
          practitioner. Once your medical bills exceed the cashback amount for
          the year, the insurance starts paying. Any unused cashback is yours to
          keep!
        </p>
        <div className="mt24 mb16">
          {!!mainMonthlyCashback && (
            <p className="p-p d-flex jc-between ws3">
              You
              <span>
                {englishFormattedEuroCurrency(mainMonthlyCashback, true)}/month
              </span>
            </p>
          )}
          {!!spouseMonthlyCashback && (
            <p className="p-p d-flex jc-between ws3">
              Spouse
              <span>
                {englishFormattedEuroCurrency(spouseMonthlyCashback, true)}
                /month
              </span>
            </p>
          )}
          {childrenMonthlyCashbackValues?.map(
            (guaranteedMonthlyCashback, index) => (
              <p
                className="p-p d-flex jc-between ws3"
                key={String(`CHILD_CB_${index}`)}
              >
                Child{' '}
                {childrenMonthlyCashbackValues.length > 1 ? `${index + 1}` : ''}{' '}
                <span>
                  {englishFormattedEuroCurrency(
                    guaranteedMonthlyCashback,
                    true
                  )}
                  /month
                </span>
              </p>
            )
          )}
          <p className="p-p d-flex jc-between ws3 fw-bold">
            Total
            <span>
              {englishFormattedEuroCurrency(totalMonthlyCashback, true)}/month
            </span>
          </p>
        </div>
        <div className="mt24 mb16 d-flex jc-between">
          <p className="p-h3">No-claim-in-a-year bonus</p>
          <p className="p-h3">
            {englishFormattedEuroCurrency(totalFirstYearNoClaimsBonus, true)}
          </p>
        </div>
        <div className={`p-p ${styles.noClaimsContainer}`}>
          <p>Each family member can get this bonus if they</p>
          <ul>
            <li>submitted no claims for a whole calendar year</li>
            <li>were covered for all 12 months of that year</li>
            <li>are still covered for the following year</li>
          </ul>
          <p className="mt24">
            The first eligible year starts on{' '}
            <span className="fw-bold">January 1st</span>
          </p>
          <p>
            This reward will increase if you don’t submit claims for multiple
            years in a row. If you go to the doctor, simply pay out-of-pocket to
            stay eligible for the bonus. If you change your mind, you can always
            submit the bills up to 2 years later. Some conditions apply.
          </p>

          <p className="p-p mt16">Your family’s bonus:</p>
          {yearlyNoClaimsBonuses.map((value, index) => (
            <p className="p-p" key={value}>
              {englishFormattedEuroCurrency(value, true)} after {index + 1} year
              {index === 0 ? '' : 's'}
            </p>
          ))}
          <p className="p-p">
            {englishFormattedEuroCurrency(yearlyNoClaimsBonuses[4], true)} for
            every other year
          </p>
        </div>
        <div className={`mt24 d-flex jc-between ${styles.divider}`}>
          <p className="p-h3">Potential cashback for first year</p>
          <p className="p-h2">
            {englishFormattedEuroCurrency(totalPotentialCashback, true)}
          </p>
        </div>
      </div>
    </BottomOrRegularModal>
  );
};
